/**
 * ░░░░░▄▀▀▀▄░░░░░░░░░░░░░░░░░
 * ▄███▀░◐░░░▌░░░░░░░░░░░░░░░░
 * ░░░░▌░░░░░▐░░░░░░░░░░░░░░░░
 * ░░░░▐░░░░░▐░░░░░░░░░░░░░░░░
 * ░░░░▌░░░░░▐▄▄░░░░░░░░░░░░░░
 * ░░░░▌░░░░▄▀▒▒▀▀▀▀▄░░░░░░░░░
 * ░░░▐░░░░▐▒▒▒▒▒▒▒▒▀▀▄░░░░░░░
 * ░░░▐░░░░▐▄▒▒▒▒▒▒▒▒▒▒▀▄░░░░░
 * ░░░░▀▄░░░░▀▄▒▒▒▒▒▒▒▒▒▒▀▄░░░
 * ░░░░░░▀▄▄▄▄▄█▄▄▄▄▄▄▄▄▄▄▄▀▄░
 * ░░░░░░░░░░░▌▌░▌▌░░░░░░░░░░░
 * ░░░░░░░░░░░▌▌░▌▌░░░░░░░░░░░
 * ░░░░░░░░░▄▄▌▌▄▌▌░░░░░░░░░░░
 *
 * MIGRATION NOTICE
 *
 * We are in the process of re-writing our components using function components
 * and React Hooks, rather than extending Component.
 *
 * We have found that hooks make our components better, and - once you get the
 * hang of them - produce fewer bugs and edge cases than conventional lifecycle
 * methods (like componentDidMount and getDerivedStateFromProps).
 *
 * However, hooks can’t be used from class components. Therefore, we are trying
 * to eliminate class components wherever we can.
 *
 * If you’re changing this file, please consider contributing to this effort by
 * changing this component to be a function that uses hooks, and then removing
 * this notice.
 */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { css, cx } from 'emotion';

import analytics from 'site-react/core/Analytics';
import GlobalEvents from 'site-react/core/Events/GlobalEvents';
import theme from 'site-react/theme';

import AccountMenu from './DesktopNavMenuAccount';
import MainMenu from './DesktopNavMenuMain';

const desktopNavStyles = css`
  display: none;

  @media (min-width: ${theme.breakpoints.lg}px) {
    align-items: center;
    display: flex;
  }
`;

class DesktopNav extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activePanel: '',
    };

    this.analyticsData = {
      location: 'meganav',
    };

    this.globalEvents = new GlobalEvents({
      click: this.onAnyClick.bind(this),
    });

    this.closeAllPanels = this.closeAllPanels.bind(this);
    this.onNavListItemClick = this.onNavListItemClick.bind(this);
  }

  componentDidMount() {
    this.globalEvents.listen();
  }

  componentWillUnmount() {
    this.globalEvents.remove();
  }

  onAnyClick(event) {
    if (event.target.id === 'locations' || event.target.id === 'guides') {
      this.setActivePanel(event.target.id);
      return;
    }

    if (
      event.target.closest('#locations-panel') ||
      event.target.closest('#guides-panel')
    ) {
      return;
    }

    this.closeAllPanels();
  }

  /**
   * Handle nav list item clicks
   * @param  {Object} event The event object
   * @param  {Object} item The data
   */
  onNavListItemClick(event, item) {
    event.preventDefault();

    const { logoutFunc } = this.props;

    this.analyticsHandler(item);
    // If a panel don't do a redirect
    if (item.panelName) {
      return;
    }
    // If it's the logout link, do that first
    if (item.onClick === 'logout') {
      logoutFunc();
    } else if (item.url.includes('/signup') || item.url.includes('/login')) {
      const redirectUrl = new URL(item.url, window.location.href);
      redirectUrl.searchParams.set('next', window.location.pathname);
      window.location = redirectUrl.toString();
    } else {
      window.location = `${item.url}`;
    }
  }

  setActivePanel(panelId) {
    this.setState({
      activePanel: panelId,
    });
  }

  /**
   * Send analytics events
   *
   * @param  {Object} item - Item clicked on
   */
  analyticsHandler(item) {
    if (!item.analytics) return;
    const eventData = item.analytics.data
      ? { ...this.analyticsData, ...item.analytics.data(item) }
      : this.analyticsData;

    analytics.track(item.analytics.event, eventData);
  }

  closeAllPanels() {
    this.setState({ activePanel: '' });
  }

  render() {
    const { isLoggedIn, user } = this.props;
    const { activePanel } = this.state;
    return (
      <div className={cx(desktopNavStyles, 'test-DesktopNav')}>
        <MainMenu
          activePanel={activePanel}
          onNavListItemClick={this.onNavListItemClick}
        />
        <AccountMenu
          isLoggedIn={isLoggedIn}
          onNavListItemClick={this.onNavListItemClick}
          user={user}
        />
      </div>
    );
  }
}

DesktopNav.defaultProps = {
  isLoggedIn: false,
  logoutFunc: null,
  user: {
    name: '',
    avatarUrl: '',
  },
};

DesktopNav.propTypes = {
  isLoggedIn: PropTypes.bool,
  logoutFunc: PropTypes.func,
  user: PropTypes.shape({
    name: PropTypes.string,
    avatarUrl: PropTypes.string,
  }),
};

export default DesktopNav;
